import api from './api';
import { ApiResponse, Invoice, PaginationParams, Contract } from '../types';

/** Resposta de GET formatted-message com channel=email */
export interface FormattedInvoiceEmailMessage {
  subject: string;
  body: string;
}

export interface InvoiceFormData {
  cliente_id: number;
  contrato_id?: number | null;
  data_vencimento: string;
  tipo: 'mensalidade' | 'servicos' | 'outros';
  valor: number;
  acrescimo?: number;
  desconto?: number;
  descricao: string;
  mes_referencia?: string;
  data_inicial?: string | null;
  data_final?: string | null;
  observacoes?: string;
  gateway_id?: number | null;
  naogeranfcom?: boolean | null;
  selectedCredits?: number[];
}

export interface InvoiceFilters extends PaginationParams {
  status?: string;
  tipo?: string;
  cliente_id?: number;
  contrato_id?: number;
  data_inicio?: string;
  data_fim?: string;
  data_pagamento_inicio?: string;
  data_pagamento_fim?: string;
  data_criacao_inicio?: string;
  data_criacao_fim?: string;
  gateway_id?: number;
  /** Quando true, retorna apenas faturas com gateway_id nulo (não combinar com gateway_id numérico) */
  sem_gateway?: boolean;
  forma_pagamento?: string;
  naogeranfcom?: boolean;
  sem_nfcom?: boolean;
  geranfcom_contrato?: boolean;
  status_cliente?: string;
  /** Filtro por usuário coletor (campo usuarioId_coletor na fatura) */
  usuarioId_coletor?: number;
  fatura_id?: number;
  cliente_nome?: string;
  contrato_titulo?: string;
  /** Quando true, retorna apenas faturas pagas dentro do prazo (data_pagamento <= data_vencimento) */
  pago_no_prazo?: boolean;
}

class InvoiceService {
  // Listar todas as faturas com paginação e filtros
  async getInvoices(filters: InvoiceFilters = {}): Promise<ApiResponse<Invoice[]>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/invoices?${params.toString()}`);
    return response as unknown as ApiResponse<Invoice[]>;
  }

  // Buscar fatura por ID
  async getInvoiceById(id: number): Promise<ApiResponse<Invoice>> {
    const response = await api.get(`/invoices/${id}`);

    // O interceptor do axios retorna response.data, que já é o objeto ApiResponse do backend
    if (response && typeof response === 'object' && 'success' in response) {
      return response as ApiResponse<Invoice>;
    }

    throw new Error('Formato de resposta inválido da API');
  }

  // Buscar faturas por cliente
  async getInvoicesByClient(clientId: number): Promise<ApiResponse<Invoice[]>> {
    const response = await api.get(`/invoices/client/${clientId}`);

    // O interceptor do axios retorna response.data, que já é o objeto ApiResponse do backend
    // Verificar se a resposta tem a estrutura esperada
    if (response && typeof response === 'object') {
      // Se tem propriedade success, é uma ApiResponse válida
      if ('success' in response) {
        return response as ApiResponse<Invoice[]>;
      }

      // Se é um array diretamente, normalizar para ApiResponse
      if (Array.isArray(response)) {
        return {
          success: true,
          data: response as Invoice[],
          message: 'Faturas carregadas com sucesso'
        };
      }
    }

    // Fallback: tratar como erro
    throw new Error('Formato de resposta inválido da API');
  }

  // Buscar faturas por contrato
  async getInvoicesByContract(contractId: number): Promise<ApiResponse<Invoice[]>> {
    const response = await api.get(`/invoices/contract/${contractId}`);

    // O interceptor do axios retorna response.data, que já é o objeto ApiResponse do backend
    // Verificar se a resposta tem a estrutura esperada
    if (response && typeof response === 'object') {
      // Se tem propriedade success, é uma ApiResponse válida
      if ('success' in response) {
        return response as ApiResponse<Invoice[]>;
      }

      // Se é um array diretamente, normalizar para ApiResponse
      if (Array.isArray(response)) {
        return {
          success: true,
          data: response as Invoice[],
          message: 'Faturas do contrato carregadas com sucesso'
        };
      }
    }

    // Fallback: tratar como erro
    throw new Error('Formato de resposta inválido da API');
  }

  // Buscar faturas pendentes
  async getPendingInvoices(): Promise<ApiResponse<Invoice[]>> {
    const response = await api.get('/invoices/pending');

    // O interceptor do axios retorna response.data, que já é o objeto ApiResponse do backend
    if (response && typeof response === 'object' && 'success' in response) {
      return response as ApiResponse<Invoice[]>;
    }

    throw new Error('Formato de resposta inválido da API');
  }

  // Buscar faturas vencidas
  async getOverdueInvoices(): Promise<ApiResponse<Invoice[]>> {
    const response = await api.get('/invoices/overdue');

    // O interceptor do axios retorna response.data, que já é o objeto ApiResponse do backend
    if (response && typeof response === 'object' && 'success' in response) {
      return response as ApiResponse<Invoice[]>;
    }

    throw new Error('Formato de resposta inválido da API');
  }

  // Buscar contratos de um cliente
  async getClientContracts(clientId: number): Promise<ApiResponse<Contract[]>> {
    const response = await api.get(`/invoices/contracts/${clientId}`);

    // O interceptor do axios retorna response.data, que já é o objeto ApiResponse do backend
    if (response && typeof response === 'object' && 'success' in response) {
      return response as ApiResponse<Contract[]>;
    }

    throw new Error('Formato de resposta inválido da API');
  }

  // Criar nova fatura
  async createInvoice(invoiceData: InvoiceFormData): Promise<ApiResponse<Invoice>> {
    const response = await api.post('/invoices', invoiceData);

    // O interceptor do axios retorna response.data, que já é o objeto ApiResponse do backend
    if (response && typeof response === 'object' && 'success' in response) {
      return response as ApiResponse<Invoice>;
    }

    throw new Error('Formato de resposta inválido da API');
  }

  // Atualizar fatura
  async updateInvoice(id: number, invoiceData: Partial<InvoiceFormData>): Promise<ApiResponse<Invoice>> {
    const response = await api.put(`/invoices/${id}`, invoiceData);

    // O interceptor do axios retorna response.data, que já é o objeto ApiResponse do backend
    if (response && typeof response === 'object' && 'success' in response) {
      return response as ApiResponse<Invoice>;
    }

    throw new Error('Formato de resposta inválido da API');
  }

  // Marcar fatura como paga
  /**
   * @deprecated Use processPayment() instead. This method will be removed in future versions.
   * Método mantido apenas para compatibilidade com código legado.
   */
  async markAsPaid(id: number, paymentMethod: string, transactionId?: string): Promise<ApiResponse<Invoice>> {
    console.warn('⚠️ markAsPaid() está DEPRECIADO. Use processPayment() no lugar.');
    const response = await api.patch(`/invoices/${id}/pay`, {
      paymentMethod,
      transactionId
    });

    // O interceptor do axios retorna response.data, que já é o objeto ApiResponse do backend
    if (response && typeof response === 'object' && 'success' in response) {
      return response as ApiResponse<Invoice>;
    }

    throw new Error('Formato de resposta inválido da API');
  }

  /**
   * Processar pagamento de uma fatura usando o InvoicePaymentController
   * Método unificado que substitui markAsPaid() e markAsPartiallyPaid()
   */
  async receberPagamento(
    id: number,
    paymentData: {
      paymentMethod: string;
      transactionId?: string;
      paidAmount?: number;
      notes?: string;
      paymentDate?: string;
      selectedCredits?: number[];
      creditDiscount?: number;
      excessAmount?: number;
      shouldCreateCredit?: boolean;
      creditType?: string;
      creditDescription?: string;
      valor_acrescimo?: number; // Soma juros + multa - evita criar crédito quando excedente é acréscimo
      desconto?: number; // Desconto manual aplicado no pagamento
      motivoDescontoId?: number; // ID do motivo do desconto
    }
  ): Promise<ApiResponse<Invoice>> {
    // Usar a API unificada do InvoicePaymentController
    // Garantir valor_pago = valor digitado no formulário (não valor restante da fatura)
    const valorPago = Number(paymentData.paidAmount ?? 0);
    const response = await api.post(`/invoices/${id}/receber-pagamento`, {
      valor_pago: Math.round(valorPago * 100) / 100,
      forma_pagamento: paymentData.paymentMethod,
      data_pagamento: paymentData.paymentDate || new Date().toISOString(),
      transacao_id: paymentData.transactionId,
      observacoes: paymentData.notes,
      selectedCredits: paymentData.selectedCredits,
      creditDiscount: paymentData.creditDiscount,
      excessAmount: paymentData.excessAmount,
      shouldCreateCredit: paymentData.shouldCreateCredit,
      creditType: paymentData.creditType,
      creditDescription: paymentData.creditDescription,
      valor_acrescimo: paymentData.valor_acrescimo,
      desconto: paymentData.desconto,
      motivo_desconto_id: paymentData.motivoDescontoId
    });

    // O interceptor do axios retorna response.data, que já é o objeto ApiResponse do backend
    if (response && typeof response === 'object' && 'success' in response) {
      return response as ApiResponse<Invoice>;
    }

    throw new Error('Formato de resposta inválido da API');
  }

  // Marcar fatura como parcialmente paga
  /**
   * @deprecated Use processPayment() instead. This method will be removed in future versions.
   * Método mantido apenas para compatibilidade com código legado.
   */
  async markAsPartiallyPaid(id: number): Promise<ApiResponse<Invoice>> {
    console.warn('⚠️ markAsPartiallyPaid() está DEPRECIADO. Use processPayment() no lugar.');
    const response = await api.patch(`/invoices/${id}/partial-pay`);

    // O interceptor do axios retorna response.data, que já é o objeto ApiResponse do backend
    if (response && typeof response === 'object' && 'success' in response) {
      return response as ApiResponse<Invoice>;
    }

    throw new Error('Formato de resposta inválido da API');
  }

  // Cancelar fatura
  async cancelInvoice(id: number, motivo?: string): Promise<ApiResponse<Invoice>> {
    try {
      const payload = motivo ? { motivo } : {};
      const response = await api.patch(`/invoices/${id}/cancel`, payload);
      console.log('🔍 [INVOICE SERVICE] Resposta bruta do cancelamento:', response);

      if (response && typeof response === 'object') {
        if ('success' in response) {
          return response as ApiResponse<Invoice>;
        }

        // Se não tem success, mas tem data, normalizar para ApiResponse
        if ('data' in response) {
          return {
            success: true,
            data: response.data,
            message: 'Fatura cancelada com sucesso'
          };
        }
      }

      throw new Error('Formato de resposta inválido da API');
    } catch (error) {
      console.error('🔍 [INVOICE SERVICE] Erro ao cancelar fatura:', error);
      throw this.handleError(error);
    }
  }

  // Deletar fatura
  async deleteInvoice(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(`/invoices/${id}`);

      if (response && typeof response === 'object' && 'success' in response) {
        return response as ApiResponse<void>;
      }

      return {
        success: true,
        message: 'Fatura deletada com sucesso'
      };
    } catch (error) {
      console.error('🔍 [INVOICE SERVICE] Erro ao deletar fatura:', error);
      throw this.handleError(error);
    }
  }



  // Consultar status da fatura no gateway
  async checkGatewayStatus(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/invoices/${id}/check-gateway-status`);
      // O interceptor retorna response.data, então response já é o que precisamos
      const result = response as unknown as ApiResponse<any>;
      return result;
    } catch (error) {
      console.error('🔍 [INVOICE SERVICE] Erro na requisição:', error);
      throw error;
    }
  }

  // Calcular valor total da fatura
  calculateTotal(valor: number, acrescimo: number = 0, desconto: number = 0): number {
    return valor + acrescimo - desconto;
  }

  // Formatar status para exibição
  formatStatus(status: string): { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' } {
    const statusMap = {
      'aberto': { label: 'Aberto', color: 'info' as const },
      'pago': { label: 'Pago', color: 'success' as const },
      'vencido': { label: 'Vencido', color: 'error' as const },
      'parcialmente_pago': { label: 'Parcialmente Pago', color: 'warning' as const },
      'cancelado': { label: 'Cancelado', color: 'default' as const }
    };

    return statusMap[status as keyof typeof statusMap] || { label: status, color: 'info' };
  }

  // Formatar tipo para exibição
  formatType(tipo: string): string {
    const typeMap = {
      'mensalidade': 'Mensalidade',
      'servicos': 'Serviços',
      'outros': 'Outros'
    };

    return typeMap[tipo as keyof typeof typeMap] || tipo;
  }

  // Verificar se fatura está vencida
  isOverdue(dataVencimento: string, status: string): boolean {
    if (status === 'pago') return false;

    const today = new Date();
    const dueDate = new Date(dataVencimento);

    return dueDate < today;
  }

  // Calcular dias até vencimento
  getDaysUntilDue(dataVencimento: string): number {
    const today = new Date();
    const dueDate = new Date(dataVencimento);
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }

  // Gerar mês de referência atual
  getCurrentReferenceMonth(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  }

  // Validar dados da fatura
  validateInvoiceData(data: InvoiceFormData): string[] {
    const errors: string[] = [];

    if (!data.cliente_id || data.cliente_id <= 0) {
      errors.push('Cliente é obrigatório');
    }

    if (!data.valor || data.valor <= 0) {
      errors.push('Valor deve ser maior que zero');
    }

    if (data.acrescimo && data.acrescimo < 0) {
      errors.push('Acréscimo não pode ser negativo');
    }

    if (data.desconto && data.desconto < 0) {
      errors.push('Desconto não pode ser negativo');
    }

    if (!data.data_vencimento) {
      errors.push('Data de vencimento é obrigatória');
    }

    if (!data.tipo || !['mensalidade', 'servicos', 'outros'].includes(data.tipo)) {
      errors.push('Tipo de fatura inválido');
    }

    if (!data.descricao || data.descricao.trim().length < 5) {
      errors.push('Descrição deve ter pelo menos 5 caracteres');
    }

    // Mês de referência será preenchido automaticamente, não precisa validar no frontend

    return errors;
  }

  private handleError(error: any): Error {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }
    return new Error('Erro ao processar a requisição');
  }

  async processBoleto(invoiceId: number, gatewayId: number): Promise<ApiResponse<Invoice>> {
    try {
      const response = await api.post(`/invoices/${invoiceId}/process-boleto`, {
        gatewayId
      });

      if (response && typeof response === 'object' && 'success' in response) {
        return response as ApiResponse<Invoice>;
      }

      throw new Error('Formato de resposta inválido da API');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Estornar pagamento
  async estornarPagamento(paymentId: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(`/invoices/${paymentId}/estornar-pagamento`);

      // O interceptor do axios retorna response.data, que já é o objeto ApiResponse do backend
      if (response && typeof response === 'object' && 'success' in response) {
        return response as ApiResponse<any>;
      }

      // Se não é um ApiResponse, normalizar
      return {
        success: true,
        data: response,
        message: 'Pagamento estornado com sucesso'
      };
    } catch (error) {
      console.error('Erro ao estornar pagamento:', error);
      throw error;
    }
  }

  // Formatar valor para moeda brasileira
  formatarValor(value: number): string {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  // Formatar data para exibição
  formatarData(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Buscar dados para renegociação de faturas
   * Retorna faturas e configuração de juros/multa
   */
  async getRenegociarDados(ids: number[]): Promise<ApiResponse<{
    invoices: any[];
    configuracao: { juros: number; multa: number };
  }>> {
    const response = await api.post('/invoices/renegociar-dados', { ids });
    if (response && typeof response === 'object' && 'success' in response) {
      return response as ApiResponse<{
        invoices: any[];
        configuracao: { juros: number; multa: number };
      }>;
    }
    throw new Error('Formato de resposta inválido da API');
  }

  /**
   * Executar renegociação de faturas
   * Cancela as faturas selecionadas e cria uma nova fatura consolidada
   */
  async renegociarFaturas(data: {
    ids: number[];
    contrato_id?: number | null;
    data_vencimento: string;
    valor: number;
    acrescimo?: number;
    desconto?: number;
    gateway_id?: number | null;
    cobranca_id?: number | null;
  }): Promise<ApiResponse<{ nova_fatura_id: number; faturas_canceladas: number[] }>> {
    const response = await api.post('/invoices/renegociar', data);
    if (response && typeof response === 'object' && 'success' in response) {
      return response as ApiResponse<{ nova_fatura_id: number; faturas_canceladas: number[] }>;
    }
    throw new Error('Formato de resposta inválido da API');
  }

  // Gerar PDF das faturas
  async imprimirPDF(invoiceIds: number[], type: 'carne' | 'fatura' | 'capa_carne'): Promise<Blob> {
    try {
      const response: any = await api.post('/invoices/imprimir-pdf', {
        invoiceIds,
        type
      }, {
        responseType: 'blob'
      });

      return response; // O interceptor retorna response.data, que será o Blob
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Obtém a mensagem de notificação formatada para a fatura
   */
  async getFormattedMessage(
    id: number,
    evento: string = 'enviar_fatura',
    channel: 'whatsapp' | 'email' | 'sms' = 'whatsapp'
  ): Promise<ApiResponse<string | FormattedInvoiceEmailMessage>> {
    const response = await api.get(
      `/invoices/${id}/formatted-message?evento=${encodeURIComponent(evento)}&channel=${encodeURIComponent(channel)}`
    );

    if (response && typeof response === 'object' && 'success' in response) {
      return response as ApiResponse<string | FormattedInvoiceEmailMessage>;
    }

    throw new Error('Formato de resposta inválido da API');
  }

  /**
   * Renderiza um template de recibo com variáveis da fatura já substituídas (processado no backend)
   */
  async renderReceiptTemplate(
    invoiceId: number,
    templateId: number
  ): Promise<ApiResponse<{ html: string; templateTitle: string; invoiceId: number; clienteName: string }>> {
    const response = await api.get(
      `/invoices/${invoiceId}/render-receipt?templateId=${templateId}`
    );

    if (response && typeof response === 'object' && 'success' in response) {
      return response as ApiResponse<{ html: string; templateTitle: string; invoiceId: number; clienteName: string }>;
    }

    throw new Error('Formato de resposta inválido da API');
  }
}

export default new InvoiceService(); 